/**
 * Returns & refunds (POS Phase 3) — pick a counter sale to reverse; a full
 * credit note is raised and the customer is refunded from the drawer.
 */
import { useState } from "react";
import { toast } from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import { formatRupees } from "../invoices/lib/format-money.ts";

interface Sale {
  id: string;
  docNumber: string;
  customerName: string;
  totalPaise: string;
  issuedOn: string;
}

export function ReturnsPage({ onBack }: { onBack: () => void }) {
  const sales = trpc.invoices.list.useQuery({ status: "paid", limit: 40 });
  const [selected, setSelected] = useState<Sale | null>(null);
  const [reason, setReason] = useState("");
  const [done, setDone] = useState<{ cn: string; refund: string; awaiting: boolean } | null>(null);

  const ret = trpc.pos.returnSale.useMutation({
    onSuccess: (d) => {
      setDone({ cn: d.creditNoteDocNumber, refund: d.refundPaise, awaiting: d.refundAwaitingApproval != null });
      setSelected(null);
      setReason("");
      void sales.refetch();
      toast.success(`Refunded ${formatRupees(d.refundPaise)}`, { description: `Credit note ${d.creditNoteDocNumber}` });
    },
    onError: (e) => toast.error("Couldn't process the return", { description: e.message }),
  });

  const posSales: Sale[] = (sales.data?.items ?? [])
    .filter((s) => (s.docNumber ?? "").startsWith("POS/"))
    .map((s) => ({ id: s.id, docNumber: s.docNumber as string, customerName: s.customerName, totalPaise: s.totalPaise, issuedOn: s.issuedOn }));

  const Header = (
    <header className="flex h-14 flex-shrink-0 items-center gap-3 border-b border-line bg-paper px-4">
      <div className="grid h-8 w-8 place-items-center rounded-md bg-ink text-sm font-extrabold text-white">F</div>
      <div className="text-sm font-bold">Flint POS · Returns</div>
      <div className="flex-1" />
      <button onClick={onBack} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-line bg-paper px-3 text-sm font-semibold text-ink-2 hover:bg-bg">← Register</button>
    </header>
  );

  if (done) {
    return (
      <div className="flex min-h-screen flex-col bg-bg text-ink">
        {Header}
        <main className="mx-auto grid w-full max-w-md flex-1 place-items-center px-4">
          <div className="w-full rounded-xl border border-line bg-paper p-6 text-center shadow-sm">
            <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ok-50 text-2xl text-ok">↩</div>
            <h2 className="mt-3 text-xl font-bold">Return processed</h2>
            <p className="mt-1 text-sm text-muted">Credit note <b>{done.cn}</b> raised.</p>
            <div className="mt-4 rounded-lg border border-line bg-raised px-4 py-3">
              <div className="text-xs font-semibold text-muted">{done.awaiting ? "Refund pending manager approval" : "Refunded from the drawer"}</div>
              <div className="font-mono text-2xl font-bold">{formatRupees(done.refund)}</div>
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setDone(null)} className="h-12 flex-1 rounded-lg border border-line bg-paper font-semibold text-ink-2 hover:bg-bg">Another return</button>
              <button onClick={onBack} className="h-12 flex-1 rounded-lg bg-ink font-bold text-white hover:bg-ink-2">Back to register</button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-bg text-ink">
      {Header}
      <main className="mx-auto w-full max-w-3xl px-4 pb-24 pt-6">
        <div className="text-xs font-bold uppercase tracking-[0.12em] text-sky-700">Counter</div>
        <h1 className="mt-1.5 text-2xl font-bold tracking-tight">Returns &amp; refunds</h1>
        <p className="mt-1 text-sm text-muted">Pick a counter sale to reverse. A full credit note is raised and the customer is refunded from the drawer.</p>

        {selected ? (
          <div className="mt-5 rounded-xl border border-line bg-paper p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-sm font-bold">{selected.docNumber}</div>
                <div className="text-sm text-muted">{selected.customerName} · {selected.issuedOn}</div>
              </div>
              <div className="font-mono text-xl font-bold">{formatRupees(selected.totalPaise)}</div>
            </div>
            <div className="mt-4 mb-1.5 text-xs font-semibold text-ink-2">Reason for return</div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
              placeholder="e.g. wrong size — customer swapped at the counter."
              className="min-h-[72px] w-full resize-y rounded-md border border-line bg-paper p-2.5 text-sm text-ink outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => { setSelected(null); setReason(""); }} className="h-12 rounded-lg border border-line bg-paper px-5 font-semibold text-ink-2 hover:bg-bg">Cancel</button>
              <button
                disabled={reason.trim().length < 3 || ret.isPending}
                onClick={() => ret.mutate({ invoiceId: selected.id, reason: reason.trim() })}
                className="flex h-12 items-center justify-center rounded-lg bg-bad px-6 font-bold text-white shadow-md hover:brightness-95 disabled:opacity-50"
              >
                {ret.isPending ? "Processing…" : `Return & refund ${formatRupees(selected.totalPaise)}`}
              </button>
            </div>
          </div>
        ) : sales.isLoading ? (
          <div className="mt-6 text-sm text-muted">Loading sales…</div>
        ) : posSales.length === 0 ? (
          <div className="mt-6 rounded-xl border border-dashed border-line bg-paper p-8 text-center text-sm text-muted">
            No counter sales to return yet. Ring a sale on the register first.
          </div>
        ) : (
          <div className="mt-5 overflow-hidden rounded-xl border border-line bg-paper shadow-sm">
            {posSales.map((s, i) => (
              <div key={s.id} className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? "border-t border-line" : ""}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm font-bold">{s.docNumber}</div>
                  <div className="truncate text-xs text-muted">{s.customerName} · {s.issuedOn}</div>
                </div>
                <div className="font-mono text-sm font-bold">{formatRupees(s.totalPaise)}</div>
                <button onClick={() => setSelected(s)} className="h-9 rounded-md border border-line bg-raised px-3.5 text-sm font-semibold text-ink-2 hover:border-bad hover:bg-bad-50 hover:text-bad">Return</button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
